import type { EconomicEvidenceData, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { EconomicSourceRef, WaterUseBenchmarkSourceRef } from '../../types/economicContribution';
import { waterUseAmbientConditionsFromSedClimate } from './siteScreeningInput';
import { dryCoolingDesignScreenFromClimate, dryCoolingDesignSourceRefs, isCurrentWaterUseScreeningResult } from './waterUseScreening';
import { readTmyHottestHoursSummary, tmyHottestHoursBasisText, tmyHottestHoursSource } from './tmyCoincidentWetBulb';

export const NONCOINCIDENT_DRY_BULB_LIMITATION = 'The SED peak dry-bulb proxy and the paired hottest-TMY-hour means are different statistics, not a coincident design pair. Applying the paired TMY mean wet bulb to the SED peak dry bulb can overstate wet-bulb depression and adiabatic headroom. This temperature sensitivity does not establish annual water use, equipment duty or OEM capacity.';

/** Frozen SED evidence only. Never loads weather, changes annual quantities or mutates a model revision. */
export function derivedDryCoolingForSed(sed: SiteEvidenceDocument | null | undefined) {
  const data = sed?.economicEvidence?.data;
  const water = data?.waterUseScreening;
  if (!sed || !data || !water || water.status !== 'calculated' || !water.resolvedContext
    || !isCurrentWaterUseScreeningResult(water)
    || sed.economicEvidence?.status === 'unavailable' || sed.economicEvidence?.status === 'anomalous'
    || sed.economicEvidence?.carriedForward != null) return null;
  const ambient = waterUseAmbientConditionsFromSedClimate(sed);
  if (!ambient || !water.scenarios.some(row => row.kind === 'dry-cooling')) return null;
  const candidate = readTmyHottestHoursSummary(sed.climate.data?.tmyHottestHours, { id: water.siteId, location: sed.location });
  const paired = candidate && candidate.meanWetBulbC <= ambient.peakDryBulbC ? candidate : null;
  const screen = dryCoolingDesignScreenFromClimate(water, ambient, paired?.meanWetBulbC ?? null);
  if (!screen) return null;
  const sources: WaterUseBenchmarkSourceRef[] = [...dryCoolingDesignSourceRefs(screen)];
  if (paired) {
    const source = tmyHottestHoursSource(paired);
    sources.push({ id: `tmy-hottest-hours-${paired.summaryHash}`, title: 'Computed paired hottest-TMY-hour screening means',
      publisher: source.publisher,
      publisherClass: paired.source === 'ashrae-iwec2' ? 'standards-body'
        : paired.source === 'pvgis' ? 'public-research-body' : 'federal-laboratory',
      url: source.url,
      accessedAt: paired.fetchedAt, locator: `Validated dataset SHA-256 ${paired.datasetHash}; hottest 35 of 8760 hours`,
      // A computed table transcription, never an invented verbatim publisher quotation.
      excerpt: tmyHottestHoursBasisText(paired), excerptKind: 'table-transcription', evidenceRole: 'numeric-basis',
    });
  }
  const limitations = [...screen.limitations, ...(paired ? [NONCOINCIDENT_DRY_BULB_LIMITATION] : [])];
  return { screen: { ...screen, limitations }, ambient, paired, sources };
}

/** Read-only presentation copy. The original content/revision hashes and annual figures remain input evidence. */
export function waterScreenDataForSed(
  sed: SiteEvidenceDocument,
  derived = derivedDryCoolingForSed(sed),
): EconomicEvidenceData | undefined {
  const data = sed.economicEvidence?.data;
  if (!data || !derived || !data.waterUseScreening) return data ?? undefined;
  const water = data.waterUseScreening;
  const sources: EconomicSourceRef[] = [...(data.waterScreeningSources ?? [])];
  for (const source of derived.sources) if (!sources.some(row => row.id === source.id && row.url === source.url)) sources.push(source);
  return { ...data, waterScreeningSources: sources, waterUseScreening: {
    ...water, resolvedContext: water.resolvedContext ? { ...water.resolvedContext, ambientConditions: derived.ambient } : null,
    scenarios: water.scenarios.map(row => row.kind !== 'dry-cooling' ? row : {
      ...row, dryCoolingDesignScreen: derived.screen,
      sourceRefs: [...row.sourceRefs, ...derived.sources.filter(source => !row.sourceRefs.some(old => old.id === source.id && old.url === source.url))],
      limitations: [...row.limitations, ...derived.screen.limitations,
        ...(derived.paired ? [tmyHottestHoursBasisText(derived.paired), 'The economic content/model hashes identify the original annual and coefficient evidence; the auxiliary temperature projection is separately bound by the TMY dataset and summary hashes.'] : [])],
    }),
  } };
}
